import React, { type ComponentType } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { dispatchPageId } from 'src/app/state/dispatcher/pageId';
import { actionUpdateProgress } from 'src/app/state/modules/app/progress';
import { actionUpdateDraftProgress } from 'src/app/state/modules/formData/draftProgress';
import { actionUpdatePreviousPageName } from 'src/app/state/modules/tealium/previousPageName';
import { excludeKeys } from 'src/app/utils/excludeKeys';
import { log } from 'src/app/utils/logging';
import {
  getNextRouteByPageUid,
  getRouteByPageUid,
} from 'src/app/utils/pageUtils';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import type AppState from 'src/types/appState';

export const withNextPageRoute =
  (WrappedComponent: ComponentType<any>) => (props: any) => {
    const { pageId, progress } = useSelector((state: AppState) => ({
      pageId: state.session.pageId,
      progress: state.app.progress,
    }));
    const dispatch = useDispatch();
    const { goToRoute } = useLocalisedRoutes();

    const updateProgress = (
      currentPageId: string,
      isCurrentPageValid?: boolean,
    ) => {
      const newProgress = {
        currentPageId: progress.currentPageId,
        furthestPageId: progress.furthestPageId,
        isValidObj: {
          ...progress.isValidObj,
          [currentPageId]: isCurrentPageValid ?? true,
        },
      };

      dispatch(actionUpdateProgress(newProgress));
      dispatch(actionUpdateDraftProgress(newProgress.furthestPageId));
    };

    const handleNextPageRoute = () => {
      navigateTo(getNextRouteByPageUid(pageId));
    };

    const handleProgrammaticRouteChange = (
      nextRoutePageId: string,
      isValid?: boolean,
    ) => {
      log.info(`Navigate from ${pageId} to ${nextRoutePageId} page`);
      navigateTo(getRouteByPageUid(nextRoutePageId), isValid);
    };

    const navigateTo = (nextRoute: string, isValid: boolean = true) => {
      dispatch(actionUpdatePreviousPageName(pageId));
      dispatchPageId(dispatch, nextRoute);
      updateProgress(pageId, isValid);

      goToRoute(nextRoute);
    };

    const allProps = {
      ...props,
      handleNextPageRoute,
      handleProgrammaticRouteChange,
    };
    return <WrappedComponent {...excludeKeys(allProps, 'pageId')} />;
  };
